import {TextField} from '@hconnect/common/components'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {Stack} from '@mui/material'
import {useFormContext} from 'react-hook-form'

import {
  ChecklistStepSection,
  YesNoWithCommentField,
  TextFieldGroupWithRetake,
  YesNoItemWithRetakeAndCommentField
} from '../../components'
import {HROC_CHECKLIST_FORM_WIDTH_IN_PX} from '../../consts'
import {useWidgetTranslation} from '../../hooks'
import type {KilnCheckSheetCoolerSectionFormValues} from '../../types'
import {StepFormActionsContainer} from '../StepFormActionsContainer'

type Props = {
  readOnly?: boolean
}

export const CoolerSectionFields = ({readOnly = false}: Props) => {
  const {control} = useFormContext<KilnCheckSheetCoolerSectionFormValues>()
  const {t} = useWidgetTranslation()

  return (
    <CardBox width={{xs: '100%', md: HROC_CHECKLIST_FORM_WIDTH_IN_PX}}>
      <Stack spacing={2}>
        <ChecklistStepSection title={t('kilnCheckSheet.label.compartments')}>
          <Stack spacing={1}>
            <TextFieldGroupWithRetake
              fields={[
                {
                  name: 'compartmentsNormal',
                  label: t('kilnCheckSheet.label.compartmentsNormal'),
                  retakeName: 'compartmentsNormalRetake'
                },
                {
                  name: 'compartmentsFull',
                  label: t('kilnCheckSheet.label.compartmentsFull'),
                  retakeName: 'compartmentsFullRetake'
                }
              ]}
              commentField={{
                name: 'compartmentsComment',
                label: t('kilnCheckSheet.label.compartmentsComment')
              }}
              control={control}
              readOnly={readOnly}
            />
          </Stack>
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.grates')}>
          <Stack spacing={1}>
            <TextFieldGroupWithRetake
              fields={[
                {
                  name: 'gratesNormal',
                  label: t('kilnCheckSheet.label.gratesNormal'),
                  retakeName: 'gratesNormalRetake'
                },
                {
                  name: 'gratesMissing',
                  label: t('kilnCheckSheet.label.gratesMissing'),
                  retakeName: 'gratesMissingRetake'
                }
              ]}
              commentField={{
                name: 'gratesComment',
                label: t('kilnCheckSheet.label.gratesComment')
              }}
              control={control}
              readOnly={readOnly}
            />
          </Stack>
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.coolerDrives')}>
          <Stack gap={2}>
            <YesNoItemWithRetakeAndCommentField<KilnCheckSheetCoolerSectionFormValues>
              label={t('kilnCheckSheet.label.coolerDrivesOperatingProperly')}
              name={'coolerDrivesOperatingProperly'}
              readOnly={readOnly}
              control={control}
            />
            <YesNoItemWithRetakeAndCommentField<KilnCheckSheetCoolerSectionFormValues>
              label={t('kilnCheckSheet.label.coolerDrives3ShotsGrease')}
              name={'coolerDrives3ShotsGrease'}
              readOnly={readOnly}
              control={control}
            />
          </Stack>
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.megaBlasters')}>
          <Stack>
            <YesNoItemWithRetakeAndCommentField<KilnCheckSheetCoolerSectionFormValues>
              label={t('kilnCheckSheet.label.megaBlastersOperatingProperly')}
              name={'megaBlastersOperatingProperly'}
              readOnly={readOnly}
              control={control}
            />
          </Stack>
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.undergrateFans')}>
          <ChecklistStepSection title={t('kilnCheckSheet.label.undergrateFans1To8')}>
            <YesNoWithCommentField
              label={t('kilnCheckSheet.label.undergrateFans1To8OperatingProperly')}
              name="undergrateFans1To8OperatingProperly"
              control={control}
              readOnly={readOnly}
            />
          </ChecklistStepSection>
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.clinkerBreaker')}>
          <ChecklistStepSection title={t('kilnCheckSheet.label.beltsBearingsMotor')}>
            <YesNoWithCommentField
              label={t('kilnCheckSheet.label.clinkerBreakerBeltsBearingsMotorOperatingProperly')}
              name="clinkerBreakerBeltsBearingsMotorOperatingProperly"
              control={control}
              readOnly={readOnly}
            />
          </ChecklistStepSection>
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.plattcoValves')}>
          <YesNoWithCommentField
            label={t('kilnCheckSheet.label.plattcoValvesOperatingProperly')}
            name="plattcoValvesOperatingProperly"
            control={control}
            readOnly={readOnly}
          />
          <YesNoWithCommentField
            label={t('kilnCheckSheet.label.plattcoValvesAnyLeftOpen')}
            name="plattcoValvesAnyLeftOpen"
            control={control}
            readOnly={readOnly}
          />
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.finesDrags')}>
          <ChecklistStepSection title={t('kilnCheckSheet.label.finesDrags6119And6120')}>
            <YesNoWithCommentField
              label={t('kilnCheckSheet.label.finesDrags6119And6120OperatingProperly')}
              name="finesDrags6119And6120OperatingProperly"
              control={control}
              readOnly={readOnly}
            />
          </ChecklistStepSection>
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.panConveyor')}>
          <ChecklistStepSection title={t('kilnCheckSheet.label.panConveyorEastPan')}>
            <YesNoWithCommentField
              label={t('kilnCheckSheet.label.panConveyorEastPanOperatingProperly')}
              name="panConveyorEastPanOperatingProperly"
              control={control}
              readOnly={readOnly}
            />
            <YesNoWithCommentField
              label={t('kilnCheckSheet.label.panConveyorEastPanExcessiveSpillage')}
              name="panConveyorEastPanExcessiveSpillage"
              control={control}
              readOnly={readOnly}
            />
          </ChecklistStepSection>
          <ChecklistStepSection title={t('kilnCheckSheet.label.pans')}>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                name="panConveyorEastPanPansNormal"
                label={t('kilnCheckSheet.label.panConveyorEastPanPansNormal')}
                optional
                control={control}
                readOnly={readOnly}
              />
              <TextField
                fullWidth
                name="panConveyorEastPanPansDamaged"
                label={t('kilnCheckSheet.label.panConveyorEastPanPansDamaged')}
                optional
                control={control}
                readOnly={readOnly}
              />
            </Stack>
            <TextField
              name="panConveyorEastPanPansComment"
              label={t('kilnCheckSheet.label.panConveyorEastPanPansComment')}
              optional
              control={control}
              readOnly={readOnly}
            />
          </ChecklistStepSection>
        </ChecklistStepSection>
        <StepFormActionsContainer />
      </Stack>
    </CardBox>
  )
}
